import { FC, useCallback, useContext, useEffect, useState } from "react";
import CustomTable from "../../../../../components/table/CustomTable";
import { DocumentsService } from "../../../../../services/document/documentsService";
import { toast } from "sonner";
import useFilters from "../../../../../hooks/useFilters";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import { CensusService } from "../../../../../services/census/censusService";
import useCensusStatus from "../../../../../hooks/api-calls/useCensusStatus";
import useFetch from "../../../../../hooks/useFetch";
import moment from "moment";
import StatusDropdown from "../../../../../components/forms/StatusDropdown";
import { MdOutlineInsertPhoto, MdOutlineModeEditOutline } from "react-icons/md";
import Viewer from "react-viewer";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { Link, useNavigate, useParams } from "react-router-dom";
import { menuRoutes } from "../../../../../router/menu";
import { HiOutlineEye } from "react-icons/hi2";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { IssuesApiResponse } from "../../../../../type/entities/issues-type";
import cn from "../../../../../utils/classNames";
import { IoAddSharp, IoCheckmarkDoneSharp } from "react-icons/io5";
import { FaSpinner } from "react-icons/fa";
import CensusLinesDropdown from "../../../../census/list/CensusLinesDropdown";
import { PrivilegeContext } from "../../../../../components/priviledge/PriviledgeProvider";
import {ReactComponent as Excel} from "../../../../../assets/svg/excel.svg";

const columnOrder = [
    "id",
    'censusId',
    "date",
    "time",
    "endTime",
    "censusDpma",
    "censusMin",
    "park",
    "observationPost",
    "status",
    "statusId",
    "statusColor",
    "censusPhotos",
    "observer",
    "census_lines",
]

const ParkOFC: FC<{}> = () => {

    // STATES
    
    const [ isViewerOpen, setIsViewerOpen ] = useState(null);
    const [ isLoadingImages, setIsLoadingImages ] = useState<boolean>(false);
    const [ isValidating, setIsValidating ] = useState<boolean>(false);
    const [ viewerImages, setViewerImages] = useState<any>(null);
    
    // HOOKS
    
    const navigate = useNavigate();
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const service = new CensusService();
    const { getCensusStatusList } = useCensusStatus();
    const { userCan } = useContext(PrivilegeContext);

    // FETCH DATA

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
            if (filters?.filter_filters?.park == null || filters?.filter_filters?.park == undefined) {
                return
            }
            const response = await service.listOfc(filters);
            return response.getResponseData();
     },[filters]));

    // FUNCTIONS

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UN CENSO
     * @EN DELETE AN CENSUS
     * @param id
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteCensus(id)).getResponseData();
        if(response.success) {
            toast.success("OFC eliminado correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA TODAS LOS OFC SELECCIONADAS
     * @EN DELETES ALL SELECTED OFC
     * 
     * @param ids
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiCensus(ids)).getResponseData();
        if(response.success) {
            toast.success(response.message);
            refetch();
        }else {
            handleErrors(response);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------------
    

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENA LOS DATOS PARA QUE COINCIDAN CON LA COLUMNA
     * @EN SORTS THE DATA TO MATCH THE COLUMN
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-----------------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO DEL OFC
     * @EN CHANGE THE STATUS OF THE OFC
     * 
     * @param id
     * @param status
     */ 
    //-----------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusCensus(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado del OFC cambiado correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTAR A EXCEL
     * @EN EXPORT TO EXCEL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const exportToExcel = async () => {
        const response = await (await service.exportToExcel(filters, true)).getResponseData();
        if(response) {
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'ofcs.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        }else {
            handleErrors(response);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------------    

    const handleOpenViewer = async (ofcRow : any) => {
        setIsLoadingImages(true);
        if (!ofcRow.ofcPhotos || ofcRow.ofcPhotos.length === 0) return;
        const images = await Promise.all(
            (ofcRow.ofcPhotos || []).map(async (photo : any) => {
                const response = await new DocumentsService().renderDocument(photo.document.id);
                const file = new Blob([response?.getResponse()?.data], { type: 'image/' + photo.document.extension });
                return {
                    ...photo,
                    src: URL.createObjectURL(file),
                };
            })
        );
        setViewerImages(images);
        setIsViewerOpen(ofcRow.id);
        setIsLoadingImages(false);
    };

    // USE EFFECTS

    useEffect(() => {
        if (id) {
            resetFilters();
            updateFilters({ park: id });
        }
    }, [id]);

    // RENDER

    return (
        <div>
            <div className="mx-auto w-100 max-h-[auto] overflow-auto">
                <div className="grid grid-cols-1 gap-4">
                    <div className="md:col-span-3 col-span-1">
                        <div className="grid gap-7">
                            <CustomTable
                                divClassName='max-h-table'
                                classNameTable='fs-table'
                                headerTableClassName='fs-table-header'
                                id={"ofc-park-table"}
                                columnOrder={columnOrder}
                                data={orderedData()}
                                multiActions={[
                                    {
                                        label: 'Validar seleccionados',
                                        render: (selectedRows: any, selectedRowsOriginal: any, reset: Function) => {
                                            return (
                                                <Button
                                                    variant="outline"
                                                    color="primary"
                                                    className="flex items-center border-green text-green"   
                                                    disabled={isValidating}
                                                    onClick={async () => {
                                                        try {
                                                            setIsValidating(true);
                                                            const response = (await service.validateSelectedCensus(selectedRows));
                                                            if (response) {
                                                                toast.success('OFCS validadas correctamente');
                                                                refetch();
                                                            } else {
                                                                toast.error('Error al validar los OFCS');
                                                            }
                                                        } catch (error: any) {
                                                            toast.error(error.message);
                                                        } finally {
                                                            setIsValidating(false);
                                                            reset();
                                                        }
                                                    }}
                                                >
                                                    <IoCheckmarkDoneSharp className="h-[20px] w-[20px] mr-2" />
                                                    {isValidating ? <FaSpinner className="animate-spin h-[20px] w-[20px] mr-2" /> : <span>Validar</span>}
                                                </Button>
                                            )
                                        }
                                    }
                                ]}
                                addicionalButtons={
                                    <>
                                        {userCan('export', 'census') && (
                                            <>
                                                <Tooltip
                                                    size="sm"
                                                    content={'Exportar a Excel'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <Button
                                                        color="primary"
                                                        onClick={exportToExcel}
                                                        className={cn(['button-export-excel bg-white border-secondary text-black'])}
                                                    >
                                                        <Excel/>
                                                        <span id="export-excel" className="ms-1">Exportar Excel</span>
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                        {userCan('create', 'census') && (
                                            <>
                                                <Tooltip
                                                    size="sm"
                                                    content={'Crear OFC'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <Button
                                                        color="primary"
                                                        onClick={() => navigate(`/ofc/create/${id}`)}
                                                        className={cn(['button-create-census bg-white border-secondary text-black'])}
                                                    >
                                                        <IoAddSharp className="h-[20px] w-[20px] mr-2" />
                                                        <span id="create-census" className="ms-1">Crear OFC</span>
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                    </>
                                }
                                isLoading={loading}
                                isSortable
                                columnsNoSortables={['censusPhotos']}
                                columnsNotShown={[
                                    'censusId',
                                    'censusMin',
                                    'statusId',
                                    'statusColor',
                                    'census_lines',
                                    'date',
                                    'time',
                                    'endTime',
                                    'censusDpma',
                                    "observationPost",
                                ]}
                                overrideColumns={[
                                    // {
                                    //     key: 'censusId',
                                    //     customHeader: 'Código',
                                    //     render: (row: any) => {
                                    //         const censusId = row.row.original.censusId || '-';
                                            
                                    //         if (userCan('get', 'census')) {
                                    //             return (
                                    //                 <a href={`${menuRoutes.census.path}/${row.row.original.id}/profile/${menuRoutes.census.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                    //                     {censusId}
                                    //                 </a>
                                    //             );
                                    //         }
                                            
                                    //         return <span>{censusId}</span>;
                                    //     }
                                    // },
                                    {
                                        key: 'park',
                                        customHeader: 'Parque eólico',
                                        render: (row: any) => {
                                            return row.row.original.park || '-';
                                        }
                                    },
                                    {
                                        key: 'observer',
                                        customHeader: 'Observador',
                                        render: (row: any) => {
                                            return row.row.original.observer || '-';
                                        }
                                    },
                                    {
                                        key: 'status',
                                        render: (row: any) => {

                                            const censusStatus  = {
                                                name: row.row.original?.status || '-',
                                                id: row.row.original?.statusId || '-',
                                                color: row.row.original?.statusColor || '-',
                                            }
                                            const censusId      = row.row.original?.id;

                                            return (
                                                <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id + censusStatus.id}>
                                                    {(censusId && censusStatus) ? (
                                                        <StatusDropdown
                                                            divClassName='fs-table'
                                                            selectedClassName='fs-table'
                                                            optionsClassName='fs-table'
                                                            entityId={row.row.original?.id}
                                                            title="¿Estas seguro de cambiar el estado del Incidencias?"
                                                            statesOptions={getCensusStatusList()}
                                                            currentState={censusStatus}
                                                            disabled={!userCan('edit', 'census')}
                                                            handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(censusId, statusId)}
                                                            hasComments={false}
                                                        />
                                                    ) : "N/A"}
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        key: 'censusPhotos',
                                        customHeader: 'Fotos',
                                        render: (row: any) => {
                                            return (
                                                <p className="flex items-center gap-2">
                                                <MdOutlineInsertPhoto
                                                    className="h-4 w-4"
                                                    size={20}
                                                    onClick={() => handleOpenViewer(row.row.original)}
                                                />
                                                <span>{row.row.original.censusPhotos?.length || 0}</span>
                                                {row.row.original.censusPhotos?.length !== 0 ? <Viewer
                                                    visible={isViewerOpen === row.row.original.id}
                                                    onClose={() => setIsViewerOpen(null)}
                                                    images={viewerImages?.map((img: any) => ({ src: img.src, alt: img.document.name }))}
                                                />
                                                : null}
                                            </p>
                                            );
                                        }
                                    }
                                ]}
                                actions={[
                                    {
                                        label: 'View',
                                        permissions: { group: 'census', permission: 'get' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Ver Censo'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <Link
                                                        to={`${menuRoutes.ofc.path}/${row.id}/profile/${menuRoutes.ofc.profile.info}`}>
                                                        <ActionIcon
                                                            as="span"
                                                            size="sm"
                                                            variant="outline"
                                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-5 w-5"
                                                        >
                                                            <HiOutlineEye className="h-3 w-3" size={20} />
                                                        </ActionIcon>
                                                    </Link>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                    {
                                        label: 'Edit',
                                        permissions: { group: 'census', permission: 'edit' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Editar OFC'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <ActionIcon
                                                        as="span"
                                                        size="sm"
                                                        variant="outline"
                                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer h-5 w-5"
                                                        onClick={() => {
                                                            navigate(`${menuRoutes.ofc.path}/${row.id}/edit`);
                                                        }}
                                                    >
                                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                    {
                                        label: 'Delete',
                                        permissions: { group: 'census', permission: 'delete' },
                                        render: (row: any) => {
                                            return (
                                                <Tooltip
                                                    size="sm"
                                                    content={'Eliminar OFC'}
                                                    placement="top"
                                                    color="invert"
                                                >
                                                    <div>
                                                        <DeletePopover
                                                            actionIconClassName="h-5 w-5"
                                                            title={`Eliminar OFC`}
                                                            description={`¿Estás seguro de que deseas eliminar este OFC?`}
                                                            onDelete={() => handleDelete(row.id)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            );
                                        },
                                    },
                                ]}
                                handleMultipleDelete={handleMultiDelete}
                                renderRowDropdown={(row) => <CensusLinesDropdown row={row} />}
                                filters={filters}
                                updateFilters={updateFilters}
                                updateFilterOrder={updateFilterOrder}
                                defaultOrder={filters.filter_order || undefined}
                                paginationData={{
                                    pageSize: filters.limit,
                                    currentPage: filters.page,
                                    pageCount: (data as IssuesApiResponse) ? data.lastPage : 1,
                                    totalCount: data?.totalRegisters,
                                    handlePagination: updatePage,
                                    handlePerPage: updatePageSize,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ParkOFC;
